<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { thirdPartyService } from "@/services/internal-api/thirdparty.service";
import store from "@/state/store";
import { helpers } from "@/services/helpers";
import Loader from "@/components/widgets/loader.vue";

const baseLink = "https://trykcm.com/referral/";

const link = ref("");

const error = ref("");
const dismissCountDown = ref(0);
const dismissSecs = ref(5);
const idFail = ref(false);
const isLoading = ref(true);

async function getHubspotRecordId(): Promise<void> {
  if (store.getters["crm/hubspotId"]) {
    link.value = baseLink + store.getters["crm/hubspotId"];
    return;
  }
  const hubspotContact = await thirdPartyService
    .get(
      "/hubspot/v1/contact/" +
        store.getters["auth/authEmail"] +
        "?fields=hs_object_id"
    )
    .catch((err) => {
      helpers.customSentryError(
        "Getting hubspot data",
        "Failed to get hubspot contact data for refer-friend",
        { error: err }
      );

      error.value = "Sorry, failed to create referral link at this time.";
      idFail.value = true;
    });

  if (hubspotContact) {
    const hubspotId = hubspotContact.data?.properties?.hs_object_id
      ? hubspotContact.data.properties?.hs_object_id
      : "";
    store.dispatch("crm/setHubspotId", hubspotId);
    link.value = baseLink + hubspotId;
  }
}

const copyLink = (): void => {
  error.value = "";
  try {
    navigator.clipboard.writeText(link.value);
  } catch (err) {
    error.value = "Unable to copy to clipboard.";
  }
  dismissCountDown.value = dismissSecs.value;
};

const countDownChanged = (countDown: number): void => {
  dismissCountDown.value = countDown;
};

const alertVariant = computed((): string => {
  return error.value === "" ? "success" : "danger";
});
const alertMessage = computed((): string => {
  return error.value === "" ? "Copied to clipboard!" : error.value;
});

onMounted(async () => {
  await getHubspotRecordId();
  isLoading.value = false;
});
</script>

<template>
  <div>
    <h2 class="mb-3">KCM Referral Program</h2>
    <div class="mb-2">
      <strong>Loving KCM? Refer a friend and get rewarded!</strong>
    </div>
    <p>
      Your referral program offers rewards to loyal Members who love KCM and
      want to share KCM with the world. You’re helping us grow KCM and put
      indispensable tools and insights in agents’ hands; as a thank you, you can
      get a free month of Membership!
    </p>
    <div class="mb-2"><strong>Here’s how it works:</strong></div>
    <ol>
      <li>
        Copy and share your unique link (below) with your friends and peers
      </li>
      <li>
        When a new Member signs up using your link and makes their second
        payment, you’ll get a free month added to your account.
      </li>
    </ol>
    <div class="my-2">
      <strong>Questions on the specifics?</strong> Go
      <a target="_blank" href="https://help.mykcm.com/kcm-referral-program"
        >here.</a
      >
    </div>
    <Loader loading v-if="isLoading" classes="bg-white" />
    <div v-else-if="link" class="d-flex flex-wrap">
      <b-input class="w-75 me-1" readonly disabled v-model="link"></b-input>
      <b-button class="flex-grow-1" variant="success" @click="copyLink">
        <font-awesome-icon icon="fa-solid fa-copy" />
      </b-button>
    </div>
    <b-alert
      :show="dismissCountDown || idFail"
      :variant="alertVariant"
      class="text-center mt-2"
      @dismissed="dismissCountDown = 0"
      @dismiss-count-down="countDownChanged"
    >
      {{ alertMessage }}</b-alert
    >
  </div>
</template>
